import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSendFilter, cadenceApi, campaignApi, messageSendsApi, templateApi } from '../services/api';
import { Cadence, Campaign, MessageSend, SendStatsRow, WhatsAppTemplate } from '../types';
import { PRODUCER_LABEL, PRODUCER_PILL, SEND_PILL, errorLabel, pct } from '../lib';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import { Section, Empty, Failed, LeadCell, Loading, PageFrame, Pill, Strip, Tabs, Th, tr } from '../components/PlatformBits';
import WhatsAppPreview, { renderTemplate } from '../components/WhatsAppPreview';
import { DeliveryCell, Pager, ReplyCell, VisitCell, useReplies } from '../components/SendCells';

/** One template's sends: the numbers, then every ledger row that carried it
 *  — from any producer — newest first, server-paged; the message on the right. */

type Segment = '' | 'replied' | 'visited' | 'failed';
const PAGE = 25;

/** What a producer_ref points at, in words. */
const refLabel = (s: MessageSend, campaigns: Record<string, Campaign>, steps: Record<string, { cadence: Cadence; position: number }>): string => {
  if (s.producer === 'campaign') return campaigns[s.campaign_id || '']?.name || s.producer_ref;
  if (s.producer === 'cadence') {
    const st = steps[s.producer_ref.split(':')[2] || ''];
    return st ? `${st.cadence.name} · step ${st.position + 1}` : s.producer_ref;
  }
  if (s.producer === 'test') return 'owner probe';
  return s.delivered_as === 'freeform' ? 'free-form, inside the window' : '';
};

const TemplateSendsPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const [templates, setTemplates] = useState<WhatsAppTemplate[] | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [cadences, setCadences] = useState<Cadence[]>([]);
  const [byProducer, setByProducer] = useState<SendStatsRow[] | null>(null);
  const [page, setPage] = useState<{ total: number; data: MessageSend[] } | null>(null);
  const [producer, setProducer] = useState('');
  const [segment, setSegment] = useState<Segment>('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!templateId) return;
    Promise.all([templateApi.list(), campaignApi.listWithStats(), cadenceApi.list(), messageSendsApi.stats('producer', { template_id: templateId })])
      .then(([t, c, cd, s]) => { setTemplates(t); setCampaigns(c); setCadences(cd); setByProducer(s); })
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, [templateId]);

  useEffect(() => {
    if (!templateId) return;
    const filter: MessageSendFilter = { template_id: templateId };
    if (producer) filter.producer = producer;
    if (segment) filter.segment = segment;
    setLoading(true);
    messageSendsApi.list(filter, skip, PAGE)
      .then((p) => { setPage(p); setLoading(false); })
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, [templateId, producer, segment, skip]);

  const replies = useReplies(page?.data ?? null);

  const template = useMemo(() => templates?.find((t) => t.id === templateId) || null, [templates, templateId]);
  const parent = useMemo(() => (template?.parent_id ? templates?.find((t) => t.id === template.parent_id) : undefined), [templates, template]);
  const rendered = template ? renderTemplate(template, parent) : null;
  const campaignById = useMemo(() => Object.fromEntries(campaigns.map((c) => [c.id, c])), [campaigns]);
  const stepById = useMemo(() => {
    const out: Record<string, { cadence: Cadence; position: number }> = {};
    for (const c of cadences) for (const st of c.steps) out[st.id] = { cadence: c, position: st.position };
    return out;
  }, [cadences]);

  const totals = useMemo(() => {
    const sum = { sends: 0, delivered: 0, read: 0, replied: 0, visited: 0, failed: 0 };
    for (const r of byProducer || []) {
      sum.sends += r.sends; sum.delivered += r.delivered; sum.read += r.read; sum.replied += r.replied; sum.visited += r.visited; sum.failed += r.failed;
    }
    return sum;
  }, [byProducer]);

  if (error) return <Failed what="template" error={error} />;
  if (!templates || !byProducer) return <div className="w-full px-10 pt-8"><Loading what="template" /></div>;
  if (!template) return <Failed what="template" error="not found" />;

  const strip: StripItem[] = [
    { v: totals.visited, k: `visit${totals.visited === 1 ? '' : 's'} booked within 14 days`, cls: 'visited' },
    { v: totals.replied, k: 'replied within 7 days', p: totals.sends ? `${((100 * totals.replied) / totals.sends).toFixed(1)}%` : '0%', cls: 'gold' },
    { v: totals.sends.toLocaleString('en-IN'), k: 'sends' },
    { v: totals.delivered.toLocaleString('en-IN'), k: 'delivered', p: pct(totals.delivered, totals.sends) },
    { v: totals.read.toLocaleString('en-IN'), k: 'read', p: pct(totals.read, totals.sends) },
  ];
  if (totals.failed) strip.push({ v: totals.failed, k: 'failed', cls: 'bad' });

  const producerTabs: [string, React.ReactNode][] = [
    ['', <>All producers <span className="num opacity-80">{totals.sends.toLocaleString('en-IN')}</span></>],
    ...byProducer.filter((r) => r.key).sort((a, b) => b.sends - a.sends).map((r): [string, React.ReactNode] =>
      [r.key!, <>{PRODUCER_LABEL[r.key!] || r.key} <span className="num opacity-80">{r.sends.toLocaleString('en-IN')}</span></>]),
  ];
  const segments: [Segment, React.ReactNode][] = [
    ['', <>Everyone</>],
    ['replied', <>Replied <span className="num opacity-80">{totals.replied}</span></>],
    ['visited', <>Visited <span className="num opacity-80">{totals.visited}</span></>],
    ['failed', <>Failed <span className="num opacity-80">{totals.failed}</span></>],
  ];

  const pick = (fn: () => void) => { fn(); setSkip(0); };

  return (
    <PageFrame
      title={template.display_name}
      back={{ to: '/templates', label: 'All templates' }}
      chip={<>
        <Pill tone={template.status === 'APPROVED' ? 'ok' : 'gold'}>{template.status.toLowerCase()} at Meta</Pill>
        {template.retired_at && <Pill>retired</Pill>}
      </>}
      subline={<>
        <span className="font-mono text-[12px]">{template.name}</span>
        {template.parent_id && <> · fills <span className="text-forest font-semibold">{parent?.name || template.parent_id}</span></>}
        {template.group && <> · {template.group}</>}
      </>}
    >
      <Strip>{totals.sends ? <NumberStrip items={strip} /> : <span className="text-graphite text-[13px]">No sends on the ledger.</span>}</Strip>

      <div className="grid gap-12 items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 320px' }}>
        <Section title="Sends" note="newest first · every producer that carried this template">
          <div className="flex items-center gap-2 flex-wrap px-1 py-3">
            <Tabs value={producer} onChange={(p) => pick(() => setProducer(p))} items={producerTabs} />
            <span className="flex-1" />
            <Tabs value={segment} onChange={(sg) => pick(() => setSegment(sg))} items={segments} />
          </div>
          <table className="w-full min-w-[760px] border-collapse">
            <thead><tr>
              <Th left>Lead</Th><Th left>Producer</Th><Th left>Delivery</Th><Th left ours>Site visit</Th><Th left>Their reply</Th>
            </tr></thead>
            <tbody className={`transition-opacity ${loading && page ? 'opacity-50' : ''}`}>
              {!page && <Empty cols={5}>Loading sends…</Empty>}
              {page && page.data.length === 0 && <Empty cols={5}>No sends match.</Empty>}
              {page?.data.map((s) => (
                <tr key={s.id} className={`${tr} hover:bg-charcoal/[0.025]`}>
                  <LeadCell id={s.lead_id} name={s.lead_name}
                            sub={<span className="num">{s.lead_phone || '—'}{s.attempt_n > 1 && <span className="text-amber-ink font-semibold"> · attempt {s.attempt_n}</span>}</span>} />
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-px rounded-full border text-[11px] font-semibold whitespace-nowrap ${PRODUCER_PILL[s.producer] || PRODUCER_PILL.system}`}>{PRODUCER_LABEL[s.producer] || s.producer}</span>
                    {refLabel(s, campaignById, stepById) && (
                      <div className="text-[11.5px] text-graphite mt-1 whitespace-nowrap max-w-[240px] truncate" title={refLabel(s, campaignById, stepById)}>{refLabel(s, campaignById, stepById)}</div>
                    )}
                  </td>
                  <DeliveryCell status={s.status} pillCls={SEND_PILL[s.status]} sentAt={s.sent_at} readAt={s.read_at}
                                failure={s.status === 'failed' ? (s.error_code ? errorLabel(s.error_code) : s.error_detail || 'failed') : null}
                                freeform={s.delivered_as === 'freeform' && s.status !== 'failed'} />
                  <VisitCell visitedAt={s.visited_at} status={s.visit_task_status} />
                  <ReplyCell repliedAt={s.replied_at} rows={s.replied_at ? replies[s.id] : undefined} />
                </tr>
              ))}
            </tbody>
          </table>
          {page && <Pager skip={skip} limit={PAGE} total={page.total} onSkip={setSkip} />}
        </Section>

        <aside className="sticky top-8">
          <div className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite pb-3">As the lead sees it</div>
          {rendered ? <WhatsAppPreview r={rendered} wide /> : <span className="text-graphite text-[13px]">The shape this row fills is not loaded.</span>}
          {template.best_for && <div className="text-[12px] text-graphite mt-4 leading-relaxed"><span className="font-semibold text-charcoal">Best for</span> · {template.best_for}</div>}
        </aside>
      </div>
    </PageFrame>
  );
};

export default TemplateSendsPage;
